/**
 * Secure API Client with CSRF Protection
 * Implements Double-Submit-Cookie pattern for CSRF protection
 */

import { supabase } from '@/integrations/supabase/client';
import { incrementInFlight, decrementInFlight } from '@/lib/network-indicator';
import { logger } from '@/lib/logger';
import { z } from 'zod';
import { ensureFreshAuth, forceRefreshAuth, type EnsureFreshAuthResult } from '@/lib/auth-readiness';
import { getRefreshPromise, setRefreshPromise, clearRefreshPromise } from '@/lib/auth-refresh-gate';
import { ENVIRONMENT } from '@/lib/environment';
import { appendAuthDebugLog } from '@/lib/auth-debug';

// Get Supabase URL for Edge Functions (must be provided via env).
// Read on first use rather than at module load (CS-308) so this module can be
// imported without a backend env; the missing-env error still throws on use.
function getSupabaseUrl(): string {
  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
  if (!SUPABASE_URL) {
    throw new Error('Missing VITE_SUPABASE_URL environment variable. Configure it in your environment (.env.local)');
  }
  return SUPABASE_URL;
}

/**
 * Secure fetch wrapper that automatically includes CSRF tokens
 * for data-changing operations (POST, PUT, DELETE, PATCH)
 */
const MAX_RECOVERY_ATTEMPTS = 1;

// Shared single-flight refresh promise to gate concurrent requests
let refreshInFlight: Promise<EnsureFreshAuthResult> | null = null;

function cloneInitForReplay(init: RequestInit): RequestInit {
  // Clone headers and JSON body to allow replay after refresh
  const headers = new Headers(init.headers);
  let body = init.body as unknown;
  if (body && typeof body === 'string') {
    // Assume JSON string
  } else if (body && typeof body === 'object') {
    try { body = JSON.stringify(body as unknown); } catch { /* noop */ }
  }
  const cloned: RequestInit = { ...init, headers };
  if (typeof body === 'string') cloned.body = body;
  return cloned;
}

async function internalSecureFetch(
  input: RequestInfo,
  init: RequestInit = {},
  attempt = 0
): Promise<Response> {
  const logDebug = (event: Record<string, unknown>) => {
    appendAuthDebugLog({ type: 'secureFetch', ...event });
  };

  logDebug({ phase: 'start', url: typeof input === 'string' ? input : input.url, method: init.method || 'GET', attempt });

  // If a refresh is already in flight, await it before issuing any request
  if (refreshInFlight) {
    try {
      await refreshInFlight;
    } catch {
      // ignore; downstream will handle auth state
    }
  }
  // Also coalesce with a provider-published refresh (e.g., during refocus)
  try {
    const ext = getRefreshPromise && getRefreshPromise();
    if (ext) {
      const timeout = new Promise<void>((resolve) => setTimeout(resolve, 4000));
      await Promise.race([ext.then(() => {}), timeout]);
    }
  } catch {
    // ignore
  }

  // Get current session
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    logger.warn('secureFetch: no session found, attempting refresh');
    if (!refreshInFlight) {
      refreshInFlight = ensureFreshAuth({ source: 'secureFetch:no-session' });
    }
    const readiness = await (refreshInFlight as Promise<EnsureFreshAuthResult>);
    refreshInFlight = null;
    logDebug({ phase: 'no-session', readiness });
    if (!readiness.ok) {
      logger.error('secureFetch: unable to recover session', { readiness });
      logDebug({ phase: 'no-session-failed', readiness });
      throw new Error('No active session found. Please authenticate first.');
    }
    const refreshed = await supabase.auth.getSession();
    if (!refreshed.data.session) {
      logger.error('secureFetch: refresh succeeded but session missing');
      throw new Error('No active session found. Please authenticate first.');
    }
    if (attempt >= MAX_RECOVERY_ATTEMPTS) {
      logger.warn('secureFetch: max recovery attempts reached for session');
    } else {
      return internalSecureFetch(input, init, attempt + 1);
    }
  }

  // Extract CSRF token from user metadata (preferred) with app_metadata fallback (legacy)
  const csrfToken = (session?.user?.user_metadata?.csrf_token as string | undefined)
    ?? (session?.user?.app_metadata?.csrf_token as string | undefined);
  
  // Determine if this is a data-changing operation
  const method = (init.method || 'GET').toUpperCase();
  const isDataChanging = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);

  // Prepare headers
  const headers = new Headers(init.headers);
  
  // Add Authorization header
  headers.set('Authorization', `Bearer ${session!.access_token}`);
  
  // Construct the full URL for Supabase Edge Functions
  const functionPath = typeof input === 'string' ? input : input.url;
  
  // Remove '/functions/' (and optional '/v1/') prefix if present to avoid double prefixing
  const cleanPath = functionPath.replace(/^\/functions\/(v1\/)?/, '');

  // CSRF allowlist for authenticated, server-enforced endpoints
  // verify-access is a read-only check and is exempt to avoid bootstrapping deadlocks
  const csrfExemptEndpoints = new Set(['create-checkout-session', 'create-customer-portal', 'verify-access', 'get-subscription-status', 'auth-echo']);
  const endpointKey = cleanPath.split('?')[0];
  const requiresCSRF = isDataChanging && !csrfExemptEndpoints.has(endpointKey);

  // If CSRF is required for this endpoint but missing, fail clearly
  if (requiresCSRF && !csrfToken) {
    logger.warn('secureFetch: CSRF missing, attempting self-heal');
    const healResult = await ensureFreshAuth({ source: 'secureFetch:csrf-heal' });
    if (!healResult.ok) {
      logger.error('secureFetch: unable to recover CSRF token', {
        ok: healResult.ok,
        rotated: healResult.rotated,
        reason: healResult.reason,
        attempts: healResult.attempts,
        source: healResult.source
      });
      throw new Error('CSRF token not found in user session. Please re-authenticate.');
    }
    const healedSession = (await supabase.auth.getSession()).data.session;
    const healedToken = healedSession?.user?.user_metadata?.csrf_token as string | undefined
      ?? healedSession?.user?.app_metadata?.csrf_token as string | undefined;
    if (!healedToken) {
      logger.error('secureFetch: CSRF token still missing after heal');
      throw new Error('CSRF token not found in user session. Please re-authenticate.');
    }
    const retryInit = { ...init, headers: new Headers(init.headers) };
    retryInit.headers?.set?.('X-CSRF-Token', healedToken);
    if (attempt >= MAX_RECOVERY_ATTEMPTS) {
      logger.warn('secureFetch: max recovery attempts reached for CSRF');
    } else {
      return internalSecureFetch(input, { ...retryInit }, attempt + 1);
    }
  }

  // Always include X-CSRF-Token on mutating requests, per secure-fetch.ts contract
  if (isDataChanging && csrfToken) {
    headers.set('X-CSRF-Token', csrfToken);
  }

  // Add Content-Type if not present and body exists
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const fullUrl = `${getSupabaseUrl()}/functions/v1/${cleanPath}`;
  const initForReplay = cloneInitForReplay(init);
  
  // Perform the fetch with enhanced headers and global indicator
  incrementInFlight();
  let response: Response;
  try {
    response = await fetch(fullUrl, {
      ...initForReplay,
      headers
    });
  } finally {
    decrementInFlight();
  }

  // If unauthorized or forbidden, attempt a one-time silent refresh and retry once
  if (response.status === 401 || response.status === 403) {
    logDebug({ phase: 'auth-status', status: response.status });
    try {
      // If a provider refresh is active, prefer to await it instead of starting our own
      const published = getRefreshPromise && getRefreshPromise();
      if (published) {
        logger.debug('secureFetch: status-retry: awaiting provider refresh');
        const timeout = new Promise<void>((resolve) => setTimeout(resolve, 4000));
        await Promise.race([published.then(() => {}), timeout]);
      }
      if (!refreshInFlight) {
        refreshInFlight = ensureFreshAuth({ force: true, source: 'secureFetch:status-retry' });
      }
      const refreshResult = await (refreshInFlight as Promise<EnsureFreshAuthResult>);
      refreshInFlight = null;
      if (!refreshResult.ok) {
        logDebug({ phase: 'auth-status-retry-failed', error: refreshResult.reason, attempts: refreshResult.attempts });
        throw new Error('Unable to recover session after authorization failure.');
      }
      const refreshedSession = (await supabase.auth.getSession()).data.session;
      if (refreshedSession?.access_token) {
        const retryHeaders = new Headers(initForReplay.headers as HeadersInit);
        retryHeaders.set('Authorization', `Bearer ${refreshedSession.access_token}`);
        const nextCsrfToken = (refreshedSession.user.user_metadata?.csrf_token as string | undefined)
          ?? (refreshedSession.user.app_metadata?.csrf_token as string | undefined);
        if (isDataChanging && nextCsrfToken) {
          retryHeaders.set('X-CSRF-Token', nextCsrfToken);
        }
        incrementInFlight();
        try {
          const retry = await fetch(fullUrl, { ...initForReplay, headers: retryHeaders });
          return retry;
        } finally {
          decrementInFlight();
        }
      }
    } catch (e) {
      logger.warn('secureFetch refresh retry failed', { error: e instanceof Error ? e.message : String(e) });
      logDebug({ phase: 'auth-status-retry-failed', error: e instanceof Error ? e.message : String(e) });
    }
  }

  logDebug({ phase: 'end', url: typeof input === 'string' ? input : input.url, status: response.status, ok: response.ok });

  return response;
}

export async function secureFetch(
  input: RequestInfo,
  init: RequestInit = {}
): Promise<Response> {
  return internalSecureFetch(input, init, 0);
}

/**
 * Fetch JSON via secureFetch and validate with a Zod schema.
 * Throws if response !ok or if JSON shape is invalid.
 */
export async function fetchJson<T>(
  input: RequestInfo,
  init: RequestInit = {},
  schema: z.ZodType<T>
): Promise<T> {
  const response = await secureFetch(input, init);
  if (!response.ok) {
    let errorData: unknown = undefined;
    try {
      errorData = await response.json();
    } catch {
      // ignore
    }
    logger.error('fetchJson non-ok response', {
      status: response.status,
      statusText: response.statusText,
      errorData,
    });
    const err = errorData as { error?: string; message?: string; retryAfter?: unknown } | undefined;
    // Carry the HTTP status and any typed-error fields (`{ error, retryAfter }`,
    // CS-103/CS-104) so callers can tell timeout / rate-limit / server apart.
    const retryAfterRaw = err?.retryAfter ?? response.headers?.get?.('Retry-After') ?? undefined;
    const retryAfterNum = retryAfterRaw === undefined || retryAfterRaw === null ? NaN : Number(retryAfterRaw);
    throw Object.assign(
      new Error(err?.error || err?.message || `Request failed with status ${response.status}`),
      {
        status: response.status,
        code: typeof err?.error === 'string' ? err.error : undefined,
        retryAfter: Number.isFinite(retryAfterNum) && retryAfterNum >= 0 ? retryAfterNum : undefined,
      }
    );
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch (err) {
    logger.error('fetchJson failed to parse JSON', { err: err instanceof Error ? err.message : String(err) });
    throw new Error('Invalid JSON response');
  }

  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    logger.error('fetchJson invalid response shape', { issues: parsed.error.issues });
    throw new Error('Invalid server response');
  }
  return parsed.data;
}

/**
 * Helper function to check if a response indicates CSRF error
 */
export function isCSRFError(response: Response): boolean {
  if (response.status !== 403) return false;
  
  try {
    // Note: Response.body is a ReadableStream; parsing it directly is not reliable.
    // We conservatively return false here to avoid false positives.
    // CSRF errors are handled explicitly via status and server JSON body in handleCSRFError.
    const data = {} as { error?: string; message?: string };
    const isCSRF = Boolean(data.error && data.error.includes('CSRF')) || Boolean(data.message && data.message.includes('CSRF'));
    return isCSRF;
  } catch {
    return false;
  }
}

/**
 * Helper function to handle CSRF errors gracefully
 */
export async function handleCSRFError(response: Response): Promise<never> {
  const errorData = await response.json().catch(() => ({}));
  
  // Log the error for debugging
  logger.error('CSRF protection error:', {
    status: response.status,
    error: errorData.error,
    message: errorData.message,
    fullErrorData: errorData
  });
  
  throw new Error('CSRF protection error. Please refresh the page and try again.');
} 